import snapshot from "../data/snapshot.json";
import type { AppRole } from "../lib/governance/types";

/** This is a browser simulation. None of these identities authenticate a person. */
export type DemoPersona = {
  id: string;
  name: string;
  email: string;
  jobTitle: string;
  roles: AppRole[];
};
const PERSONAS: DemoPersona[] = [
  {
    id: "demo-proposer",
    name: "Simulated Proposal Engineer",
    email: "proposal@demo.invalid",
    jobTitle: "Quality Engineer (simulated)",
    roles: ["quality_engineer"],
  },
  {
    id: "demo-quality",
    name: "Simulated Quality Approver",
    email: "quality@demo.invalid",
    jobTitle: "Quality Engineer (simulated)",
    roles: ["quality_engineer"],
  },
  {
    id: "demo-manufacturing",
    name: "Simulated Manufacturing Approver",
    email: "manufacturing@demo.invalid",
    jobTitle: "Manufacturing Engineer (simulated)",
    roles: ["manufacturing_engineer"],
  },
  {
    id: "demo-director",
    name: "Simulated Quality Director",
    email: "director@demo.invalid",
    jobTitle: "Quality Director (simulated)",
    roles: ["quality_director"],
  },
  {
    id: "demo-admin",
    name: "Simulated Administrator",
    email: "admin@demo.invalid",
    jobTitle: "Administrator (simulated)",
    roles: ["admin"],
  },
  {
    id: "demo-viewer",
    name: "Simulated Viewer",
    email: "viewer@demo.invalid",
    jobTitle: "Viewer (simulated)",
    roles: ["viewer"],
  },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Runtime rows are finite, validated exported JSON shapes; source inference is retained by the typed public adapter.
export type Row = Record<string, any>;
export type Tables = Record<string, Row[]>;
export type LocalFile = { data: string; type: string; name: string };
type Stored = {
  classification: "SYNTHETIC";
  version: string;
  tenantId: string;
  membershipId: string;
  tables: Tables;
  files: Record<string, LocalFile>;
  personaId: string;
};
export type DemoSession = {
  key: string;
  stored: Stored;
  epoch: number;
  active: boolean;
  pendingWrites: Record<string, { before: Row; after: unknown; actorId: string }>;
};
export type DemoResult<T = unknown> = {
  data: T | null;
  error: { message: string; code: string; details: string; hint: string } | null;
  count?: number;
  status: number;
  statusText: string;
};
let current: DemoSession | null = null;
let generation = 0;
const listeners = new Set<(event: string, session: unknown) => void>();

export const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
export const uid = () =>
  `demo-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
export const now = () => new Date().toISOString();
export function fail(message: string): never {
  throw new Error(message);
}
export function result<T>(data: T): DemoResult<T> {
  return { data, error: null, status: 200, statusText: "OK" };
}
export function failure(error: unknown): DemoResult<never> {
  return {
    data: null,
    error: {
      code: "DEMO_LOCAL_ERROR",
      message: error instanceof Error ? error.message : String(error),
      details: "Local synthetic simulation only",
      hint: "No service request was sent.",
    },
    status: 400,
    statusText: "Demo action refused",
  };
}
function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}
function baseline(tenantId: string, membershipId: string): Stored {
  if (snapshot.classification !== "SYNTHETIC") fail("The demo requires a SYNTHETIC snapshot.");
  const tables = clone(snapshot.tables) as Tables;
  for (const p of PERSONAS) {
    tables.profiles = (tables.profiles ?? []).filter((row) => row.id !== p.id);
    tables.profiles.push({
      id: p.id,
      full_name: p.name,
      job_title: p.jobTitle,
      email: p.email,
      created_at: now(),
      updated_at: now(),
    });
    tables.user_roles = (tables.user_roles ?? []).filter((row) => row.user_id !== p.id);
    tables.user_roles.push(
      ...p.roles.map((role) => ({ id: uid(), user_id: p.id, role, granted_at: now() })),
    );
  }
  return {
    classification: "SYNTHETIC",
    version: String(snapshot.version),
    tenantId,
    membershipId,
    tables,
    files: {},
    personaId: PERSONAS[0].id,
  };
}
export function activeSession(): DemoSession {
  if (!current?.active)
    fail("Open an authenticated synthetic demo session before using demo data.");
  return current;
}
export function deactivateDemoSession(): void {
  if (current) current.active = false;
  current = null;
  emit("SIGNED_OUT");
}
export function assertSession(session: DemoSession, epoch = session.epoch): void {
  if (current !== session || !session.active || session.epoch !== epoch)
    fail("The demo session changed or was cleared. Repeat the action in the active demo.");
}
export function initializeDemoSession({
  tenantId,
  membershipId,
}: {
  tenantId: string;
  membershipId: string;
}): () => void {
  if (!tenantId?.trim() || !membershipId?.trim())
    fail("An authenticated tenant and membership are required.");
  if (current) current.active = false;
  const key = `broadview:lovable-demo:${encodeURIComponent(String(snapshot.version))}:${snapshot.asOf}:${encodeURIComponent(tenantId)}:${encodeURIComponent(membershipId)}`;
  let stored = baseline(tenantId, membershipId);
  try {
    const raw = storage()?.getItem(key);
    if (raw) {
      const saved = JSON.parse(raw) as Stored;
      if (
        saved.classification === "SYNTHETIC" &&
        saved.version === String(snapshot.version) &&
        saved.tenantId === tenantId &&
        saved.membershipId === membershipId &&
        PERSONAS.some((p) => p.id === saved.personaId) &&
        Object.keys(snapshot.tables).every((t) => Array.isArray(saved.tables?.[t]))
      )
        stored = saved;
    }
  } catch {
    /* A damaged local copy starts from the synthetic baseline. */
  }
  const session: DemoSession = {
    key,
    stored,
    epoch: ++generation,
    active: true,
    pendingWrites: {},
  };
  current = session;
  emit("INITIAL_SESSION");
  return () => {
    session.active = false;
    if (current === session) {
      current = null;
      emit("SIGNED_OUT");
    }
  };
}
export function persist(session: DemoSession): void {
  assertSession(session);
  const local = storage();
  if (local) local.setItem(session.key, JSON.stringify(session.stored));
}
/** Every local command commits its state and audit rows together, or restores both. */
export function transaction<T>(session: DemoSession, action: () => T): T {
  assertSession(session);
  const before = clone(session.stored);
  const pendingBefore = clone(session.pendingWrites);
  try {
    const data = action();
    persist(session);
    return data;
  } catch (error) {
    session.stored = before;
    session.pendingWrites = pendingBefore;
    throw error;
  }
}
export function table(session: DemoSession, name: string): Row[] {
  assertSession(session);
  if (!Object.prototype.hasOwnProperty.call(snapshot.tables, name))
    fail(`Unsupported demo table: ${name}`);
  return session.stored.tables[name] ?? fail(`Missing demo table: ${name}`);
}
export function persona(session = activeSession()): DemoPersona {
  assertSession(session);
  const found =
    PERSONAS.find((p) => p.id === session.stored.personaId) ?? fail("Unknown simulated persona.");
  return {
    ...found,
    roles: table(session, "user_roles")
      .filter((r) => r.user_id === found.id)
      .map((r) => r.role as AppRole),
  };
}
export function getDemoPersonas(): DemoPersona[] {
  if (!current?.active) return [];
  return PERSONAS.map((p) => ({
    ...p,
    roles: table(current!, "user_roles")
      .filter((r) => r.user_id === p.id)
      .map((r) => r.role as AppRole),
  }));
}
export function getDemoPersonaId(): string {
  return current?.active ? current.stored.personaId : "";
}
export function setDemoPersona(id: string): void {
  const session = activeSession();
  if (!PERSONAS.some((p) => p.id === id)) fail("Unknown simulated persona.");
  transaction(session, () => {
    session.stored.personaId = id;
  });
  session.epoch = ++generation;
  emit("USER_UPDATED");
}
export function resetDemoSession(): void {
  const session = activeSession();
  const p = session.stored.personaId;
  transaction(session, () => {
    session.stored = baseline(session.stored.tenantId, session.stored.membershipId);
    session.stored.personaId = p;
    session.pendingWrites = {};
  });
  session.epoch = ++generation;
  emit("INITIAL_SESSION");
}
export function localUser(session = activeSession()) {
  const p = persona(session);
  return {
    id: p.id,
    email: p.email,
    aud: "synthetic-demo",
    role: "simulated",
    created_at: snapshot.asOf,
    app_metadata: { simulation: true },
    user_metadata: { full_name: p.name, job_title: p.jobTitle, simulation: true },
  };
}
export function subscribe(listener: (event: string, session: unknown) => void) {
  listeners.add(listener);
  return { data: { subscription: { id: uid(), unsubscribe: () => listeners.delete(listener) } } };
}
export function emit(event: string) {
  // Synthetic session metadata deliberately contains no access/refresh tokens.
  const data = current?.active ? { user: localUser(current), simulation: true } : null;
  for (const listener of listeners)
    queueMicrotask(() => {
      if (listeners.has(listener)) listener(event, data);
    });
}
export function hasRole(session: DemoSession, roles: string[]) {
  return persona(session).roles.some((role) => roles.includes(role));
}
export function requireRole(
  session: DemoSession,
  roles: string[],
  message = "The simulated persona does not hold authority for this action.",
) {
  if (!hasRole(session, roles)) fail(message);
}
